import { useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Icon,
  Tag,
  TagLabel,
  Textarea,
  Switch,
  Tooltip,
  Spinner,
  useToast,
} from '@chakra-ui/react'
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiEdit3,
  FiGlobe,
  FiLock,
  FiTrash2,
  FiAlertCircle,
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { FileNote, FileNoteVisibility } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { UseFileNotesResult } from '@/hooks/useFileNotes'

const MAX_NOTE_LENGTH = 5000
const CLAMP_THRESHOLD = 220

function VisibilityToggle({
  value,
  onChange,
}: {
  value: FileNoteVisibility
  onChange: (value: FileNoteVisibility) => void
}) {
  const { t } = useTranslation('files')
  const isPublic = value === 'public'

  return (
    <Tooltip
      label={isPublic ? t('previewSidebar.notes.publicHint') : t('previewSidebar.notes.privateHint')}
      placement="top"
      hasArrow
    >
      <HStack as="label" spacing={1.5} cursor="pointer">
        <Switch
          size="sm"
          colorScheme="brand"
          isChecked={isPublic}
          onChange={(e) => onChange(e.target.checked ? 'public' : 'private')}
        />
        <Icon as={isPublic ? FiGlobe : FiLock} boxSize={3.5} color={isPublic ? 'brand.500' : 'gray.500'} />
        <Text fontSize="xs" color="gray.600">
          {isPublic ? t('previewSidebar.notes.public') : t('previewSidebar.notes.private')}
        </Text>
      </HStack>
    </Tooltip>
  )
}

function NoteCard({
  note,
  isOwn,
  onUpdate,
  onDelete,
}: {
  note: FileNote
  isOwn: boolean
  onUpdate: (noteId: number, data: { content: string; visibility: FileNoteVisibility }) => Promise<boolean>
  onDelete: (noteId: number) => Promise<void>
}) {
  const { t } = useTranslation('files')
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(note.content)
  const [draftVisibility, setDraftVisibility] = useState<FileNoteVisibility>(note.visibility)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showFull, setShowFull] = useState(false)

  const isPublic = note.visibility === 'public'
  const isLong = note.content.length > CLAMP_THRESHOLD || note.content.split('\n').length > 4

  const startEditing = () => {
    setDraft(note.content)
    setDraftVisibility(note.visibility)
    setIsEditing(true)
  }

  const handleSave = async () => {
    setIsSaving(true)
    const ok = await onUpdate(note.id, { content: draft.trim(), visibility: draftVisibility })
    setIsSaving(false)
    if (ok) setIsEditing(false)
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    await onDelete(note.id)
    setIsDeleting(false)
  }

  return (
    <Box bg="gray.50" borderRadius="md" p={2.5}>
      {isEditing ? (
        <VStack align="stretch" spacing={2}>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="sm"
            bg="white"
            borderRadius="md"
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
            resize="vertical"
          />
          <HStack justify="space-between">
            <VisibilityToggle value={draftVisibility} onChange={setDraftVisibility} />
            <HStack spacing={1}>
              <Button size="xs" variant="ghost" onClick={() => setIsEditing(false)}>
                {t('previewSidebar.notes.cancel')}
              </Button>
              <Button
                size="xs"
                colorScheme="brand"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!draft.trim()}
              >
                {t('previewSidebar.notes.save')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      ) : (
        <VStack align="stretch" spacing={1.5}>
          <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap" noOfLines={showFull ? undefined : 4}>
            {note.content}
          </Text>
          {isLong && (
            <Button
              size="xs"
              variant="link"
              colorScheme="brand"
              alignSelf="flex-start"
              fontWeight="normal"
              onClick={() => setShowFull(prev => !prev)}
            >
              {showFull ? t('previewSidebar.showLess') : t('previewSidebar.showMore')}
            </Button>
          )}
          <HStack justify="space-between" spacing={2}>
            <HStack spacing={1.5} minW={0}>
              <Tag size="sm" variant="subtle" colorScheme={isPublic ? 'green' : 'gray'} borderRadius="full" flexShrink={0}>
                <Icon as={isPublic ? FiGlobe : FiLock} boxSize={2.5} mr={1} />
                <TagLabel fontSize="2xs">
                  {isPublic ? t('previewSidebar.notes.public') : t('previewSidebar.notes.private')}
                </TagLabel>
              </Tag>
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {note.author.name} · {new Date(note.updated_at).toLocaleDateString()}
              </Text>
            </HStack>
            {isOwn && (
              <HStack spacing={0} flexShrink={0}>
                <IconButton
                  icon={<Icon as={FiEdit2} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'brand.500' }}
                  aria-label={t('previewSidebar.notes.edit')}
                  onClick={startEditing}
                />
                <IconButton
                  icon={<Icon as={FiTrash2} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'red.400' }}
                  aria-label={t('previewSidebar.notes.delete')}
                  onClick={handleDelete}
                  isLoading={isDeleting}
                />
              </HStack>
            )}
          </HStack>
        </VStack>
      )}
    </Box>
  )
}

export default function NotesSection({
  isOpen,
  onToggle,
  notesState,
}: {
  isOpen: boolean
  onToggle: () => void
  notesState: UseFileNotesResult
}) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const { user } = useAuth()
  const { notes, isLoading, hasError, addNote, updateNote, deleteNote, reload } = notesState

  const [draft, setDraft] = useState('')
  const [draftVisibility, setDraftVisibility] = useState<FileNoteVisibility>('private')
  const [isSaving, setIsSaving] = useState(false)

  const handleAdd = async () => {
    setIsSaving(true)
    try {
      await addNote(draft.trim(), draftVisibility)
      setDraft('')
      setDraftVisibility('private')
      toast({ title: t('previewSidebar.notes.saveSuccess'), status: 'success', duration: 2000, isClosable: true })
    } catch {
      toast({ title: t('previewSidebar.notes.saveError'), status: 'error', duration: 3000, isClosable: true })
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdate = async (noteId: number, data: { content: string; visibility: FileNoteVisibility }) => {
    try {
      await updateNote(noteId, data)
      toast({ title: t('previewSidebar.notes.saveSuccess'), status: 'success', duration: 2000, isClosable: true })
      return true
    } catch {
      toast({ title: t('previewSidebar.notes.saveError'), status: 'error', duration: 3000, isClosable: true })
      return false
    }
  }

  const handleDelete = async (noteId: number) => {
    try {
      await deleteNote(noteId)
      toast({ title: t('previewSidebar.notes.deleteSuccess'), status: 'success', duration: 2000, isClosable: true })
    } catch {
      toast({ title: t('previewSidebar.notes.deleteError'), status: 'error', duration: 3000, isClosable: true })
    }
  }

  return (
    <Box display="flex" flexDirection="column" flex={1} minH={0}>
      <HStack
        px={4}
        py={3}
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'gray.50' }}
        userSelect="none"
        flexShrink={0}
      >
        <HStack spacing={2}>
          <Icon as={FiEdit3} color="brand.500" boxSize={4} />
          <Text fontSize="sm" fontWeight="600" color="gray.700">
            {t('previewSidebar.notes.title')}
          </Text>
          {notes.length > 0 && (
            <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
              <TagLabel fontSize="xs">{notes.length}</TagLabel>
            </Tag>
          )}
        </HStack>
        <Icon as={isOpen ? FiChevronUp : FiChevronDown} color="gray.400" boxSize={4} />
      </HStack>

      {isOpen && (
        <Box
          px={4}
          pb={3}
          flex={1}
          minH={0}
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
          }}
        >
          <VStack align="stretch" spacing={2}>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('previewSidebar.notes.addPlaceholder')}
              size="sm"
              bg="white"
              borderRadius="md"
              rows={2}
              maxLength={MAX_NOTE_LENGTH}
              resize="vertical"
            />
            <HStack justify="space-between">
              <VisibilityToggle value={draftVisibility} onChange={setDraftVisibility} />
              <Button
                size="xs"
                colorScheme="brand"
                onClick={handleAdd}
                isLoading={isSaving}
                isDisabled={!draft.trim()}
              >
                {t('previewSidebar.notes.save')}
              </Button>
            </HStack>

            {isLoading ? (
              <HStack spacing={2} py={2} justify="center">
                <Spinner size="sm" color="brand.500" />
              </HStack>
            ) : hasError ? (
              <HStack spacing={2} py={2}>
                <Icon as={FiAlertCircle} color="gray.400" boxSize={4} />
                <Text fontSize="sm" color="gray.500">{t('previewSidebar.notes.loadError')}</Text>
                <Button size="xs" variant="link" colorScheme="brand" onClick={reload}>
                  {t('previewSidebar.notes.retry')}
                </Button>
              </HStack>
            ) : notes.length === 0 ? (
              <Text fontSize="sm" color="gray.500" py={1}>
                {t('previewSidebar.notes.empty')}
              </Text>
            ) : (
              notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  isOwn={note.author.id === user?.id}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              ))
            )}
          </VStack>
        </Box>
      )}
    </Box>
  )
}
